// file: src/auth/session.js
// Lean + safe: instant redirect on 401/403 or when tokens are *removed/cleared*,
// without spamming events or slowing the app.

/**
 * Notes for cookie-based landlord auth:
 * - We no longer store a landlord access token; we only keep landlord_user + remember flag.
 * - Therefore, this watcher should treat landlord_user as "authenticated" signal for landlord flows.
 * - Also, an explicit "auth:expired" event must ALWAYS redirect (ignore the hadToken gate).
 */

const TOKEN_KEYS = [
  // Admin (local/session storage tokens)
  "access_token",
  "token_type",
  "admin_user",
  // Landlord (cookie-based; keep only UI state)
  "landlord_user",
  "landlord_remember_me",
  // Back-compat keys (safe to clear if present)
  "landlord_access_token",
  "landlord_token_type",
  // Towing (if you have a separate local token)
  "auth_access_token",
  "auth_token_type",
  "auth_user",
  "auth_remember",
];

// Keys used to *detect* if we currently appear authenticated.
// For landlord (cookie-based), use the user snapshot as the presence probe.
const TOKEN_PROBES = [
  "auth_access_token", // admin/towing legacy (if used)
  "access_token", // admin legacy
  "landlord_user", // landlord cookie session
  "auth_user", // ✅ towing cookie session (UI snapshot)
];

const LANDLORD_LOGIN_PATH_DEFAULT = "/landlord/signin";
const TOWING_LOGIN_PATH = "/towing/login";
const ADMIN_LOGIN_PATH = "/admin/login";

/* ----------------------------- probe & clear ----------------------------- */
function anyTokenPresent() {
  try {
    return TOKEN_PROBES.some(
      (k) => localStorage.getItem(k) || sessionStorage.getItem(k)
    );
  } catch {
    return false;
  }
}

export function clearAllAuth() {
  try {
    TOKEN_KEYS.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  } catch {}
}

/* --------------------------------- routing -------------------------------- */
export function inferLoginPathFromLocation() {
  try {
    const p = window.location?.pathname || "";
    const landlordOverride =
      (typeof window !== "undefined" && window.__LANDLORD_LOGIN_PATH) ||
      LANDLORD_LOGIN_PATH_DEFAULT;
    if (p.startsWith("/towing")) return TOWING_LOGIN_PATH;
    if (p.startsWith("/landlord")) return landlordOverride;
  } catch {}
  return ADMIN_LOGIN_PATH;
}

/* ---------------------------- redirect (debounced) ---------------------------- */
let _redirecting = false;
let _redirectScheduled = false;

function scheduleRedirect(to) {
  if (_redirecting || _redirectScheduled) return;
  _redirectScheduled = true;
  queueMicrotask(() => {
    _redirectScheduled = false;
    if (_redirecting) return;
    _redirecting = true;

    const target = to || inferLoginPathFromLocation();
    try {
      if (window.location?.replace) {
        window.location.replace(target);
        return;
      }
    } finally {
      if (typeof window !== "undefined") window.location.href = target;
    }
  });
}

export function hardRedirectToLogin(customPath) {
  scheduleRedirect(customPath);
}

/* --------------------------------- events --------------------------------- */
export function emitAuthExpired(source = "unknown") {
  try {
    window.dispatchEvent?.(
      new CustomEvent("auth:expired", { detail: { source, forced: true } })
    );
  } catch {}
}

/* ----------------------------- same-tab storage ---------------------------- */
// Patch ONLY 'removeItem' and 'clear' (NOT 'setItem') to avoid heavy event storms.
function patchStorageOnce() {
  if (typeof window === "undefined") return;
  if (window.__STORAGE_PATCHED__) return;
  window.__STORAGE_PATCHED__ = true;

  const dispatchLocalChange = () => {
    try {
      window.dispatchEvent(new CustomEvent("storage:local-change"));
    } catch {}
  };

  const wrap = (method) => {
    const orig = Storage.prototype[method];
    if (typeof orig !== "function") return;
    Object.defineProperty(Storage.prototype, method, {
      configurable: true,
      writable: true,
      value: function (...args) {
        const r = orig.apply(this, args);
        // same-tab mutations don’t fire native 'storage'
        dispatchLocalChange();
        return r;
      },
    });
  };

  try {
    wrap("removeItem");
    wrap("clear");
  } catch {
    /* ignore redefine errors */
  }
}

/* -------------------------------- installer -------------------------------- */
export function installGlobal401Watch() {
  if (typeof window === "undefined") return;
  if (window.__GLOBAL_401_WATCH_INSTALLED__) return;
  window.__GLOBAL_401_WATCH_INSTALLED__ = true;

  patchStorageOnce();

  // Track last auth state to avoid repeated work
  let hadToken = anyTokenPresent();

  const handleExpiry = (force = false) => {
    // For explicit expiry events, force redirect regardless of stored tokens.
    if (!force) {
      // Only act if we *had* a token; prevents loops on already-logged-out pages
      if (!hadToken) return;
    }
    hadToken = false;
    clearAllAuth();
    scheduleRedirect(); // debounced + guarded
  };

  const handleStorageChange = () => {
    const now = anyTokenPresent();
    if (hadToken && !now) {
      handleExpiry(false);
    } else {
      hadToken = now;
    }
  };

  // Unified expiry channel(s). Force redirect when the app tells us auth expired.
  window.addEventListener("auth:expired", (e) => {
    const forced = !!e?.detail?.forced;
    handleExpiry(true /* force */ || forced);
  });
  // Back-compat custom event name
  window.addEventListener("landlord:auth:expired", () => handleExpiry(true));

  // Cross-tab + same-tab removals/clears
  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("storage:local-change", handleStorageChange);

  // Fallbacks: if DevTools cleared storage quietly, re-check on focus/visibility.
  window.addEventListener("focus", handleStorageChange, { passive: true });
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.visibilityState === "visible") handleStorageChange();
    },
    { passive: true }
  );
}

export default {
  clearAllAuth,
  inferLoginPathFromLocation,
  hardRedirectToLogin,
  emitAuthExpired,
  installGlobal401Watch,
};
